import { useState } from 'react';
import { message, Modal, Typography } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { dismissUpgradeRequest } from '@/api/admin';
import { getPlanLabelParts } from '@/helpers/subscription';
import { Franchise } from '@/types';

interface Props {
  // Undefined while the parent page's franchisor query is still loading.
  franchise?: Partial<Franchise>;
}

// TCX-843: contextual indicator + Dismiss action for a pending
// Connect→premium upgrade request. Renders NOTHING unless the franchise has a
// pending request (product constraint: this is not another standing General
// Info toggle), and disappears once the request resolves — whether via this
// dismiss, a Stripe plan change, or another admin. The plan name/interval come
// from the TCX-839 snapshot fields, so the label needs no live plans fetch.
// Dismissal has zero Stripe/billing side effects — the backend only clears
// the request flags.
export default function PendingUpgradeRequestLine({ franchise }: Props) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const queryClient = useQueryClient();

  const { base, suffix } = getPlanLabelParts(
    franchise?.upgradeRequestedPlanName,
    franchise?.upgradeRequestedPlanInterval,
  );
  const planLabel = suffix ? `${base} ${suffix}` : base;

  const dismissMutation = useMutation({
    mutationFn: () => dismissUpgradeRequest(franchise?.id || ''),
    onSuccess: (result) => {
      // dismissed:false = the request had already resolved (a Stripe plan
      // change cleared it, or another admin dismissed first). Safe no-op
      // server-side — say so instead of claiming this click did it, since a
      // plan change means billing DID change.
      if (result?.dismissed) {
        message.success('Upgrade request dismissed.');
      } else {
        message.info('This request was already resolved.');
      }
      setConfirmOpen(false);
    },
    onError: (error: Error) => {
      // No cache was touched, so the indicator stays — the request is intact.
      message.error(error?.message || 'Failed to dismiss the upgrade request');
    },
    onSettled: () => {
      // Refetch regardless of outcome: on success the indicator disappears;
      // on the already-resolved path this pulls the up-to-date state in.
      queryClient.invalidateQueries({
        queryKey: ['franchisor', franchise?.id],
      });
    },
  });

  if (!franchise?.upgradeRequested) {
    return null;
  }

  return (
    <>
      {/* Lean indicator (product call, 2026-07-15): just the label + the
          action — the sidebar is too narrow for the plan name, and the
          confirm modal names it anyway (that part is AC-pinned). */}
      <div className={'details-section-line'}>
        <Typography.Text className={'general-info-text'}>
          Pending Upgrade Request
        </Typography.Text>
        <Typography.Text
          className={'rating-link'}
          style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          onClick={() => setConfirmOpen(true)}
        >
          Dismiss
        </Typography.Text>
      </div>
      <Modal
        title={'Dismiss Upgrade Request'}
        open={confirmOpen}
        okText={'Dismiss'}
        confirmLoading={dismissMutation.isPending}
        cancelButtonProps={{ disabled: dismissMutation.isPending }}
        onOk={() => {
          // confirmLoading already disables the button; this guard covers a
          // queued second click racing the disable.
          if (!dismissMutation.isPending) {
            dismissMutation.mutate();
          }
        }}
        onCancel={() => {
          if (!dismissMutation.isPending) {
            setConfirmOpen(false);
          }
        }}
      >
        <Typography.Text>
          Dismiss the pending upgrade request to{' '}
          <strong>{planLabel}</strong>? This unlocks the subscription controls
          for the franchisor. No billing changes will be made.
        </Typography.Text>
      </Modal>
    </>
  );
}
